import { useLayoutDialog } from "~/store/components/layout-dialog.store";
import type { IPricingItem, IPricingModal, IStripePayload, TTransactionType } from "../types";
import { useSubAccountStore } from "~/modules/sub-account/store";
import { useAuthStore } from "~/features/authentication/store";
import { useUserStore } from "~/store/global/user.store";

type TSubscribePayload = { plansDetails: IPricingItem; planTransitionType: TTransactionType };

export const usePricingSubscribe = (selectedPlan?: TSubscribePayload) => {
    const { t } = useI18n();
    const { $clientPosthog } = useNuxtApp();
    const { openDialog } = useLayoutDialog();
    const { getModalData, getStripeLink } = usePricingStore();
    const { subAccountData } = storeToRefs(useSubAccountStore());
    const { userData } = storeToRefs(useUserStore());
    const auth = useAuth();
    const {
        activeCycle,
        showCreditPackDialog,
        showOverlay,
        expirationDateOnDowngrade,
        selectedPricePackage,
        currentCurrency,
    } = storeToRefs(usePricingStore());

    const { showAuthModal } = storeToRefs(useAuthStore());
    const modalData = useState<IPricingModal>("pricingModalData");
    const tempSubscribePayload = useState<TSubscribePayload>("tempSubscribePayload");

    const handleSubAccountSubscription = () => {
        subAccountData.value.showState = true;
        subAccountData.value.type = "limited";
    };

    const selectedPlanData = computed(() => selectedPlan || tempSubscribePayload.value);

    const openDowngradeProcessModal = () => {
        openDialog({
            title: t("pages_pricing_downGradeProgress"),

            component: shallowRef(
                defineAsyncComponent({
                    loader: () => import("../components/AppDowngradeProcessModal.vue"),
                })
            ),
            data: {
                isYearly: activeCycle.value,
            },
        });
    };
    const enterpriseQuantityValue = computed(() => {
        return selectedPlanData.value.plansDetails.enterprise
            ? activeCycle.value === "yearly"
                ? selectedPricePackage.value.credit_per_month * 12
                : selectedPricePackage.value.credit_per_month
            : undefined;
    });
    const openSubscriptionModal = (planStatus: string) => {
        openDialog({
            title:
                modalData.value?.plan_transition_type === "none"
                    ? `${t("pages_pricing_yearly_push_subscribe_to")} ${t(modalData.value.plan_title)}`
                    : `${planStatus.charAt(0).toLocaleUpperCase() + planStatus.slice(1)} Summary`,

            component: shallowRef(
                defineAsyncComponent({
                    loader: () =>
                        import(
                            planStatus === "none"
                                ? "../components/AppYearlyPushModal.vue"
                                : planStatus === "upgrade"
                                ? "../components/AppUpgradeSubscription.vue"
                                : "../components/AppDowngradeSubscription.vue"
                        ),
                })
            ),
            data: {
                ...modalData.value,
                planId: selectedPlanData.value.plansDetails.id,
                currency: currentCurrency.value,
                isYearly: activeCycle.value,
                quantity: enterpriseQuantityValue.value,
            },
        });
    };

    const subscribeStarterPlans = async () => {
        const payload: IStripePayload = {
            currency: currentCurrency.value,
            plan_id: selectedPlanData.value.plansDetails.id,
            quantity: enterpriseQuantityValue.value,
        };
        await getStripeLink(payload);
        showOverlay.value = false;
        // state.value = "default";
    };
    const handleSelectedPricingItem = async () => {
        if (selectedPlanData.value.plansDetails.cycle === "yearly" && userData.value.free_plan) {
            showOverlay.value = true;
            await subscribeStarterPlans();
        } else {
            const { data } = await getModalData(
                selectedPlanData.value.plansDetails.id,
                currentCurrency.value,
                enterpriseQuantityValue.value
            );
            modalData.value = data.value as IPricingModal;
            openSubscriptionModal(modalData.value?.plan_transition_type as string);
            expirationDateOnDowngrade.value = modalData.value?.plan_expire_date;
        }
    };

    const handleSubscribe = () => {
        if (!auth.value) {
            showAuthModal.value = true;
            tempSubscribePayload.value = {
                plansDetails: selectedPlanData.value.plansDetails,
                planTransitionType: selectedPlanData.value.planTransitionType,
            };
            return;
        }

        if (userData.value.domain === "subaccount") return handleSubAccountSubscription();
        // @ts-ignore
        $clientPosthog?.capture("aihd_subscribe_clicked");
        if (selectedPlanData.value.planTransitionType === "credit-pack") return (showCreditPackDialog.value = true);

        if (selectedPlanData.value.planTransitionType === "downgrade-pending") return openDowngradeProcessModal();

        handleSelectedPricingItem();
    };

    return {
        tempSubscribePayload,
        handleSubscribe,
    };
};
